import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAccessToken } from '../../../auth';
import { envConfig } from '../../../config';
import '../publishing-dialog.css';
import SmallCheckmark from '../../../assets/small-status/checkmark.svg';
import SmallCrossmark from '../../../assets/small-status/error.svg';
import SmallLoader from '../../../assets/small-status/loading.svg';
import SmallSkippedIcon from '../../../assets/small-status/skipped.svg';
import {
	getEventNameFromDefinition,
	getStepNameFromDefinition,
	resolvePublicationStepStatusFromType
} from '../utils';
import {
	ILog,
	PublicationStatus,
	PublicationStepStatus
} from '@elyx-code/definitions';
import { EditorService } from '../../../services/editor';
import {
	PublicationRun,
	PublicationStep
} from '../../../services/editor/publication/publication';
import DialogLoader from '../../DialogLoader';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Logger } from '@elyx-code/common-ts-utils';
import PublicationLogsViewer from './PublicationLogsViewer';

interface IPublishingSequenceProps {
	project: EditorService;
	onClose: (event: any) => void;
}

const resolveStatusIconByStatus = (
	status: PublicationStepStatus | PublicationStatus
) => {
	switch (status) {
		case PublicationStepStatus.Success:
		case PublicationStatus.Success:
			return SmallCheckmark;
		case PublicationStepStatus.Failed:
		case PublicationStatus.Failed:
		case PublicationStatus.UnrecoverableFailure:
		case PublicationStatus.CleanedUp:
			return SmallCrossmark;
		case PublicationStepStatus.Running:
		case PublicationStatus.CleanupInProgress:
			return SmallLoader;
		case PublicationStepStatus.Skipped:
			return SmallSkippedIcon;
		default:
			return;
	}
};

const getColorFromStatus = (
	status: PublicationStepStatus | PublicationStatus
): string => {
	switch (status) {
		case PublicationStepStatus.Success:
		case PublicationStatus.Success:
			return 'var(--color-main-success)';
		case PublicationStepStatus.Failed:
		case PublicationStatus.Failed:
		case PublicationStatus.UnrecoverableFailure:
		case PublicationStatus.CleanedUp:
			return '#EE0202';
		case PublicationStepStatus.Running:
		case PublicationStatus.CleanupInProgress:
			return '#FFD335';
		case PublicationStepStatus.Skipped:
			return '#7B7B7B';
		default:
			return '#7B7B7B';
	}
};

const StepTimer: React.FC<{ step: PublicationStep }> = ({ step }) => {
	const [elapsed, setElapsed] = useState<number | null>(null);

	useEffect(() => {
		if (step.status === PublicationStepStatus.Running) {
			const interval = setInterval(() => {
				const start = step.startedAt
					? new Date(step.startedAt).getTime()
					: step.createdAt
						? new Date(step.createdAt).getTime()
						: Date.now();
				const now = Date.now();
				setElapsed(Math.max(0, Math.floor((now - start) / 1000)));
			}, 1000);

			// Run immediately once
			const start = step.startedAt
				? new Date(step.startedAt).getTime()
				: step.createdAt
					? new Date(step.createdAt).getTime()
					: Date.now();
			const now = Date.now();
			setElapsed(Math.max(0, Math.floor((now - start) / 1000)));

			return () => clearInterval(interval);
		} else if (
			step.status === PublicationStepStatus.Success ||
			step.status === PublicationStepStatus.Failed
		) {
			if (step.startedAt && step.updatedAt) {
				const start = new Date(step.startedAt).getTime();
				const end = new Date(step.updatedAt).getTime();
				setElapsed(Math.max(0, Math.floor((end - start) / 1000)));
			} else {
				setElapsed(null);
			}
		} else {
			setElapsed(null);
		}
	}, [step.status, step.startedAt, step.updatedAt, step.createdAt]);

	if (elapsed === null) {
		return null;
	}

	const formatDuration = (seconds: number): string => {
		if (seconds < 60) {
			return `${seconds}s`;
		}
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		if (secs === 0) {
			return `${mins}min`;
		}
		return `${mins}min ${secs}s`;
	};

	if (step.status === PublicationStepStatus.Running) {
		return (
			<span
				style={{ fontSize: '11px', fontWeight: 600, color: '#FFD335' }}
			>
				{formatDuration(elapsed)}
			</span>
		);
	}

	return (
		<span
			style={{
				fontSize: '11px',
				color: 'rgba(0,0,0,0.38)',
				fontWeight: 'lighter',
				display: 'flex',
				gap: '8px'
			}}
		>
			<span>—</span>
			<span>Run for {formatDuration(elapsed)}</span>
		</span>
	);
};

const PublishingSequenceContent: React.FC<IPublishingSequenceProps> = ({
	project,
	onClose
}) => {
	const [activePub, setActivePub] = useState(project.publication);

	const onSpecificPublicationLoaded = useCallback((pub: PublicationRun) => {
		setActivePub(pub);
	}, []);

	useEffect(() => {
		project.on('specific-publication-loaded', onSpecificPublicationLoaded);

		return () => {
			project.off(
				'specific-publication-loaded',
				onSpecificPublicationLoaded
			);
		};
	}, [onSpecificPublicationLoaded, project]);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	const sendPublicationRequest = async () => {
		await project.publish();
	};

	useEffect(() => {
		const publicationUpdatedListener = () => {
			Logger.log('Rerendering publication: ', project.publication?.steps);
			forceUpdate();
		};

		project.on('publication-updated', publicationUpdatedListener);

		// If the URL doesn't have an id after the /editor/{projectId}/publication/publication-sequence/:here
		let publicationId = null;

		if (window.location.href.includes('publication/publication-sequence')) {
			const url = window.location.href.split('/');
			const indexOfPublicationSequence = url.indexOf(
				'publication-sequence'
			);
			publicationId = url[indexOfPublicationSequence + 1];
		}

		if (
			publicationId &&
			!!project.publication &&
			project.publication.id === publicationId
		) {
			// Do nothing, we already have the publication
			if (activePub?.id !== publicationId) {
				setActivePub(project.publication);
			}

			return;
		} else if (
			publicationId &&
			(!project.publication || project.publication.id !== publicationId)
		) {
			// Fetch existing publication
			project.loadGivenPublication(publicationId);
		} else if (!publicationId && !project.publication) {
			// if the publication has not started yet, we need to start it

			sendPublicationRequest();
		}

		return () => {
			project.off('publication-updated', publicationUpdatedListener);
		};
	}, [window.location.href, project]);

	if (!project) {
		return (
			<DialogLoader
				title="Project publication"
				subtitle="Publish your project to the cloud"
				onClose={onClose}
			/>
		);
	}

	if (!activePub) {
		return (
			<Box
				sx={{
					display: 'flex',
					// flexGrow: 1,
					flexDirection: 'column',
					gap: 6,
					alignItems: 'center',
					height: 'calc(100% - 130px)',
					width: '100%',
					justifyContent: 'center'
				}}
			>
				<CircularProgress size={120} thickness={2} />

				<Typography variant="h6">Loading publication...</Typography>
			</Box>
		);
	}

	const renderedSteps =
		activePub?.status === PublicationStatus.CleanupInProgress ||
		activePub?.status === PublicationStatus.CleanedUp ||
		activePub?.status === PublicationStatus.UnrecoverableFailure
			? [...(activePub?.steps || []), ...(activePub?.cleanupSteps || [])]
			: activePub?.steps || [];

	return (
		<div style={{ marginTop: '30px', marginBottom: '30px' }}>
			<ul style={{ padding: '0px', margin: '0px', listStyle: 'none' }}>
				{renderedSteps.map((step: PublicationStep, i: number) => (
					<li key={step.id} style={{ display: 'flex', gap: '16px' }}>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								alignSelf: 'stretch'
							}}
						>
							{step.status === PublicationStepStatus.Running ? (
								<Box
									style={{
										width: '18px',
										height: '18px',
										borderRadius: '100%',
										backgroundColor: '#FFD335',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center'
									}}
								>
									<CircularProgress
										size={10}
										thickness={6}
										sx={{ color: '#FFFFFF' }}
									/>
								</Box>
							) : step.status !== PublicationStepStatus.Queued ? (
								<img
									src={resolveStatusIconByStatus(
										step.status as PublicationStepStatus
									)}
									alt="status icon"
									style={{
										display: 'block',
										width: '18px',
										height: '18px'
									}}
								/>
							) : (
								<div
									style={{
										width: '18px',
										height: '18px',
										borderRadius: '100%',
										border: '#7B7B7B solid 3px',
										boxSizing: 'border-box',
										display: 'block'
									}}
								/>
							)}
							{(i !== renderedSteps.length - 1 ||
								!!activePub.finalStatus) && (
								// vertical line to connect the steps
								<div
									style={{
										width: '2px',
										flex: 1,
										backgroundColor:
											i !== renderedSteps.length - 1
												? getColorFromStatus(
														renderedSteps[i + 1]
															.status
													)
												: getColorFromStatus(
														activePub.finalStatus
													)
									}}
								/>
							)}
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: '12px',
								flex: 1,
								minWidth: 0
							}}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px'
								}}
							>
								<h5
									style={{
										margin: '0px',
										fontSize: '14px',
										fontWeight:
											step.status !==
											PublicationStepStatus.Running
												? 'lighter'
												: 'normal',
										color:
											step.status !==
											PublicationStepStatus.Running
												? 'rgba(0,0,0,0.54)'
												: 'inherit'
									}}
								>
									{getStepNameFromDefinition(step.stepType)}
								</h5>
								<StepTimer step={step} />
							</div>
							<p
								style={{
									margin: '0px',
									fontSize: '10px',
									marginTop: '2px',
									color: 'rgba(0,0,0,0.54)'
								}}
							>
								{resolvePublicationStepStatusFromType(
									step.status
								) || 'Queued'}
								{!!step.lastEvent &&
									' - ' +
										getEventNameFromDefinition(
											step.lastEvent
										)}
							</p>

							{/* Logs Viewer */}
							<PublicationLogsViewer
								step={step}
								isOngoing={
									(activePub.status ===
										PublicationStatus.Running ||
										activePub.status ===
											PublicationStatus.CleanupInProgress) &&
									![
										PublicationStatus.Success,
										PublicationStatus.Failed,
										PublicationStatus.Reverted,
										PublicationStatus.Unpublished,
										PublicationStatus.UnrecoverableFailure
									].includes(activePub.finalStatus)
								}
							/>
						</div>
					</li>
				))}

				{!!activePub.finalStatus && (
					<li style={{ display: 'flex', gap: '16px' }}>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center'
							}}
						>
							<img
								src={resolveStatusIconByStatus(
									activePub.finalStatus
								)}
								alt="status icon"
								style={{
									display: 'block',
									width: '18px',
									height: '18px'
								}}
							/>
						</div>
						<div
							style={{ display: 'flex', flexDirection: 'column' }}
						>
							<h5 style={{ margin: '0px', fontSize: '14px' }}>
								{getEventNameFromDefinition(
									activePub.finalGlobalEvent
								)}
							</h5>
							<p
								style={{
									margin: '0px',
									fontSize: '10px',
									marginTop: '2px',
									color: 'rgba(0,0,0,0.54)'
								}}
							>
								{activePub.finalStatus ===
									PublicationStatus.Failed ||
								activePub.finalStatus ===
									PublicationStatus.CleanedUp ||
								activePub.finalStatus ===
									PublicationStatus.UnrecoverableFailure
									? 'Unexpected error occurred. All other steps have been skipped. Try again later'
									: 'Project published successfully. You can close this window.'}
							</p>
						</div>
					</li>
				)}
			</ul>
		</div>
	);
};

export default PublishingSequenceContent;
